define("ROOM4.CCAWPhotoSwipeLoader.CCAWPhotoSwipeLoader", [
  "Utils",
], function (Utils) {
  "use strict";

  /**
   * @typedef {Object} CCAWPhotoSwipeLoaderContainer
   * @property {function(string): {isPageGenerator: function(): boolean}|null|undefined} getComponent
   */

  /**
   * One slide of a lightbox data source.
   * @typedef {Object} PhotoSwipeSlideData
   * @property {string} src url of the full-size image
   * @property {number} width intrinsic pixel width (placeholder is fine; can be corrected on loadComplete)
   * @property {number} height intrinsic pixel height
   * @property {string} [alt]
   */

  /**
   * Subset of PhotoSwipeLightbox options used by consumers. Any other
   * documented PhotoSwipe 5 option may be passed through as well.
   * @typedef {Object} PhotoSwipeLightboxOptions
   * @property {PhotoSwipeSlideData[]} [dataSource]
   * @see https://photoswipe.com/options/
   */

  /**
   * Payload of PhotoSwipe content events such as 'loadComplete'.
   * @typedef {Object} PhotoSwipeContentEvent
   * @property {{ element: HTMLImageElement|undefined, data: PhotoSwipeSlideData, index: number }} content
   */

  /**
   * Core PhotoSwipe instance, available as lightbox.pswp while open.
   * @typedef {Object} PhotoSwipeInstance
   * @property {function(number): void} refreshSlideContent re-measures a slide after its data changed
   * @property {{ data: PhotoSwipeSlideData }} [currSlide]
   */

  /**
   * PhotoSwipeLightbox instance returned by createLightbox().
   * @typedef {Object} PhotoSwipeLightboxInstance
   * @property {function(): void} init binds triggers; call once before loadAndOpen
   * @property {function(number): void} loadAndOpen opens the lightbox at a slide index
   * @property {function(): void} destroy
   * @property {function(string, function(PhotoSwipeContentEvent): void): void} on
   * @property {PhotoSwipeLightboxOptions} options mutable; set options.dataSource before loadAndOpen
   * @property {PhotoSwipeInstance|null} pswp core instance, null while closed
   * @see https://photoswipe.com/methods/
   */

  /**
   * Runtime API exposed by ccaw-photoswipe-bridge.js on window.CCAWPhotoSwipe.
   * createLightbox pre-wires pswpModule to the bundled photoswipe.esm.min.js,
   * so consumers only supply their own options.
   * @typedef {Object} CCAWPhotoSwipeBridgeApi
   * @property {function(PhotoSwipeLightboxOptions=): PhotoSwipeLightboxInstance} createLightbox
   */

  /** @type {Promise<CCAWPhotoSwipeBridgeApi>|undefined} */
  var loadPromise;
  var isLoading = false;
  var bridgeGlobalName = "CCAWPhotoSwipe";

  /**
   * Typed accessor for the global the bridge script assigns; Window has no
   * string index signature, so it is read through a string-indexable cast.
   * @returns {CCAWPhotoSwipeBridgeApi|undefined}
   */
  function getBridgeGlobal() {
    if (typeof window === "undefined") {
      return undefined;
    }

    return /** @type {CCAWPhotoSwipeBridgeApi|undefined} */ (
      /** @type {Object<string, *>} */ (window)[bridgeGlobalName]
    );
  }

  /**
   * @returns {boolean}
   */
  function supportsModuleScripts() {
    return (
      typeof document !== "undefined" &&
      "noModule" in document.createElement("script")
    );
  }

  /**
   * @param {CCAWPhotoSwipeLoaderContainer|null|undefined} container
   * @returns {boolean}
   */
  function isPageGenerator(container) {
    var Environment = container && container.getComponent("Environment");

    return !!(Environment && Environment.isPageGenerator());
  }

  /**
   * @param {string} path
   * @returns {string}
   */
  function getAssetUrl(path) {
    return Utils.getAbsoluteUrl(getExtensionAssetsPath(path));
  }

  /**
   * @returns {void}
   */
  function appendStylesheet() {
    var existingStylesheet =
      document.querySelector &&
      document.querySelector('link[data-ccaw-photoswipe="stylesheet"]');
    var styles;

    if (existingStylesheet) {
      return;
    }

    styles = document.createElement("link");
    styles.href = getAssetUrl("packages/photoswipe.css");
    styles.rel = "stylesheet";
    styles.setAttribute("data-ccaw-photoswipe", "stylesheet");
    document.head.appendChild(styles);
  }

  /**
   * @param {function(CCAWPhotoSwipeBridgeApi): void} resolve
   * @param {function(Error): void} reject
   * @returns {void}
   */
  function appendBridgeScript(resolve, reject) {
    var script = document.createElement("script");

    script.src = getAssetUrl("packages/ccaw-photoswipe-bridge.js");
    script.type = "module";
    script.async = true;
    script.setAttribute("data-ccaw-photoswipe", "bridge");
    /**
     * @returns {void}
     */
    script.onload = function () {
      var bridge = getBridgeGlobal();

      if (bridge) {
        resolve(bridge);
      } else {
        reject(new Error("PhotoSwipe bridge loaded without exposing API"));
      }
    };
    /**
     * @returns {void}
     */
    script.onerror = function () {
      reject(new Error("Failed to load PhotoSwipe bridge"));
    };

    document.head.appendChild(script);
  }

  return {
    /**
     * Whether the lightbox can work in the current environment (browser with
     * ES module support, not the SEO page generator).
     * @param {CCAWPhotoSwipeLoaderContainer|null|undefined} container
     * @returns {boolean}
     */
    canLoad: function canLoad(container) {
      return (
        typeof window !== "undefined" &&
        typeof document !== "undefined" &&
        !isPageGenerator(container) &&
        supportsModuleScripts()
      );
    },

    /**
     * Lazily injects the PhotoSwipe CSS and bridge script on first call and
     * resolves with the bridge API. The result (including a rejection) is
     * cached: every subsequent call returns the same promise.
     * @param {CCAWPhotoSwipeLoaderContainer|null|undefined} container
     * @returns {Promise<CCAWPhotoSwipeBridgeApi>}
     */
    load: function load(container) {
      var self = this;

      if (!loadPromise) {
        /**
         * @param {function(CCAWPhotoSwipeBridgeApi): void} resolve
         * @param {function(Error): void} reject
         * @returns {void}
         */
        loadPromise = new Promise(function (resolve, reject) {
          var bridge = getBridgeGlobal();

          if (bridge) {
            resolve(bridge);
            return;
          }

          if (!self.canLoad(container)) {
            reject(new Error("PhotoSwipe is not supported"));
            return;
          }

          if (!isLoading) {
            isLoading = true;
            appendStylesheet();
            appendBridgeScript(resolve, reject);
          }
        });
      }

      return loadPromise;
    },

    // assets are loaded lazily on the first load() call, nothing to do here
    /**
     * @returns {void}
     */
    mountToApp: function mountToApp() {},
  };
});
